import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, Save, Shield, TrendingUp } from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { Badge, GlassButton, GlassCard, GradientText, PageHeader } from '../ui/index.js';

interface PreflightSettings {
  guardrailsEnabled: boolean;
  guardrailMode: 'off' | 'standard' | 'strict';
  guardrailInputScreening: boolean;
  guardrailOutputScreening: boolean;
  guardrailEngine: string;
  guardrailProvider: 'openrouter' | 'self-hosted';
  guardrailBaseUrl: string;
  guardrailModel: string;
  guardrailCategories: string;
  guardrailScope: 'gateway-only' | 'all-traffic';
  financePreflightEnabled: boolean;
  financeFailMode: 'fail-open' | 'fail-closed';
  financeTimeoutMs: number;
  financeCacheTtlMs: number;
  financeMinCreditThreshold: number;
  financeWarningLevel: number;
  source: 'settings-hub' | 'local';
  scopeOrder: string[];
  secrets: Record<string, { secretId: string | null; configured: boolean; updatedAt: string | null; rotatedAt: string | null }>;
}

interface Notice { type: 'success' | 'error' | 'info'; message: string; }

function noticeClass(type: Notice['type']): string {
  switch (type) {
    case 'success': return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
    case 'error': return 'border-red-500/30 bg-red-500/10 text-red-300';
    default: return 'border-white/10 bg-white/5 text-white/70';
  }
}

export function PlatformPreflightChecksPage() {
  const [settings, setSettings] = useState<PreflightSettings | null>(null);
  const [form, setForm] = useState<PreflightSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [guardrailKeyDraft, setGuardrailKeyDraft] = useState('');
  const [revealedGuardrailKey, setRevealedGuardrailKey] = useState('');
  const [showGuardrailKey, setShowGuardrailKey] = useState(false);
  const [revealingGuardrailKey, setRevealingGuardrailKey] = useState(false);
  const [resolvingGuardrailPricing, setResolvingGuardrailPricing] = useState(false);

  async function load() {
    try {
      setLoading(true);
      setNotice(null);
      const result = await api.get<PreflightSettings>('/api/v1/platform/settings/model-gateway');
      setSettings(result);
      setForm(result);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to load pre-flight check settings.';
      setNotice({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  /**
   * Resolve guardrail model pricing from OpenRouter and apply it to all
   * catalog models with guardrail enabled, then recompute sale prices.
   * Called automatically on save when guardrails are enabled with Llama Guard.
   */
  async function resolveAndApplyGuardrailPricing(model: string, provider: string): Promise<void> {
    try {
      setResolvingGuardrailPricing(true);
      const resolveResult = await api.post<{ success: boolean; inputCostPerMillion?: number; error?: string }>(
        '/api/v1/platform/model-gateway/guardrail/resolve-pricing',
        { model, provider },
      );
      if (!resolveResult.success || resolveResult.inputCostPerMillion === undefined) return;
      const guardrailCost = resolveResult.inputCostPerMillion;

      // Fetch the current catalog and update all models with guardrail enabled
      const catalogResult = await api.get<{ models: Array<{ id: string; guardrailEnabled: boolean; [key: string]: unknown }> }>('/api/v1/platform/model-gateway/catalog');
      for (const catalogModel of catalogResult.models) {
        if (catalogModel.guardrailEnabled) {
          await api.post('/api/v1/platform/model-gateway/catalog/upsert', {
            ...catalogModel,
            guardrailCostPerMillion: guardrailCost,
          });
        }
      }
      // Recompute sale prices to include the guardrail cost
      await api.post('/api/v1/platform/model-gateway/catalog/recompute', {});
    } catch {
      // silently skip — pricing resolution is best-effort
    } finally {
      setResolvingGuardrailPricing(false);
    }
  }

  async function save() {
    if (!form) return;
    try {
      setSaving(true);
      setNotice(null);
      const saved = await api.put<PreflightSettings>('/api/v1/platform/settings/model-gateway', {
        guardrailsEnabled: form.guardrailsEnabled,
        guardrailMode: form.guardrailMode,
        guardrailInputScreening: form.guardrailInputScreening,
        guardrailOutputScreening: form.guardrailOutputScreening,
        guardrailEngine: form.guardrailEngine,
        guardrailProvider: form.guardrailProvider,
        guardrailBaseUrl: form.guardrailBaseUrl,
        guardrailModel: form.guardrailModel,
        guardrailCategories: form.guardrailCategories,
        guardrailScope: form.guardrailScope,
        ...(guardrailKeyDraft.trim() ? { guardrailApiKey: guardrailKeyDraft.trim() } : {}),
        financePreflightEnabled: form.financePreflightEnabled,
        financeFailMode: form.financeFailMode,
        financeTimeoutMs: form.financeTimeoutMs,
        financeCacheTtlMs: form.financeCacheTtlMs,
        financeMinCreditThreshold: form.financeMinCreditThreshold,
        financeWarningLevel: form.financeWarningLevel,
      });
      setSettings(saved);
      setForm(saved);
      setGuardrailKeyDraft('');
      setNotice({ type: 'success', message: 'Pre-flight check settings saved.' });

      // When guardrails are toggled ON or OFF, the global guardrailsEnabled
      // flag changes, which affects sale price computation (guardrail cost is
      // excluded when guardrails are globally disabled). Auto-recompute sale
      // prices for all catalog models so pricing reflects the new state.
      const wasEnabled = settings?.guardrailsEnabled ?? false;
      const isNowEnabled = saved.guardrailsEnabled;
      if (wasEnabled !== isNowEnabled) {
        try {
          await api.post('/api/v1/platform/model-gateway/catalog/recompute', {});
          setNotice({
            type: 'success',
            message: isNowEnabled
              ? 'Guardrails enabled. Sale prices recomputed to include guardrail cost.'
              : 'Guardrails disabled. Sale prices recomputed to exclude guardrail cost.',
          });
        } catch {
          // best-effort — the settings were saved successfully
        }
      }

      // After saving, auto-resolve guardrail pricing and push to catalog
      // (only when enabling guardrails with Llama Guard)
      if (isNowEnabled && saved.guardrailEngine === 'llama-guard') {
        void resolveAndApplyGuardrailPricing(saved.guardrailModel, saved.guardrailProvider);
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to save.';
      setNotice({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  }

  async function revealGuardrailKey() {
    try {
      setRevealingGuardrailKey(true);
      setNotice(null);
      const result = await api.post<{ target: string; value: string }>('/api/v1/platform/settings/model-gateway/reveal', { target: 'guardrailApiKey' });
      setRevealedGuardrailKey(result.value);
      setShowGuardrailKey(true);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to reveal guardrail API key.';
      setNotice({ type: 'error', message });
    } finally {
      setRevealingGuardrailKey(false);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>;
  }
  if (!form || !settings) return null;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={<GradientText>Pre-flight AI Checks</GradientText>}
        subtitle="Configure the checks that run before every AI invocation. The extension sends a preflight request to the server, which runs finance and guardrail checks before authorizing execution."
        action={
          <div className="flex items-center gap-2">
            <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void load()} loading={loading}>Refresh</GlassButton>
            <GlassButton variant="primary" icon={<Save className="h-4 w-4" />} onClick={() => void save()} loading={saving}>Save</GlassButton>
          </div>
        }
      />

      {notice && (
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-3 text-sm ${noticeClass(notice.type)}`}>
          {notice.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <span>{notice.message}</span>
        </div>
      )}

      {/* How it works */}
      <GlassCard className="space-y-3">
        <h4 className="text-sm font-semibold text-white/85">How pre-flight checks work</h4>
        <div className="space-y-2 text-xs text-white/50">
          <p>1. When a user invokes AI (any provider, not just Rule42 managed), the extension sends a preflight request to <code className="text-white/70">POST /api/v1/managed-ai/authorize-execution</code>.</p>
          <p>2. The server records telemetry for the authorization request (model, provider, estimated tokens, user, org).</p>
          <p>3. <strong className="text-white/70">Finance preflight</strong> — if enabled, checks org/user credit balance against estimated cost. If insufficient, the call is denied.</p>
          <p>4. <strong className="text-white/70">Guardrail check</strong> — if enabled, screens the prompt for safety. If blocked, the call is denied.</p>
          <p>5. If both checks pass (or are disabled), the call is authorized and the extension proceeds with the invocation.</p>
          <p>6. All decisions (allow/deny/skipped) are recorded in telemetry with status <code className="text-white/70">success</code> or <code className="text-white/70">blocked</code>.</p>
        </div>
      </GlassCard>

      {/* === Guardrail Moderation === */}
      <GlassCard className="space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-indigo-300" />
          <h3 className="text-sm font-semibold text-white">Guardrail Moderation</h3>
          <Badge variant={form.guardrailsEnabled ? 'success' : 'warning'}>{form.guardrailsEnabled ? 'Enabled' : 'Disabled'}</Badge>
        </div>

        {/* Enable toggle — always visible */}
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-white/85">Enable guardrails</p>
            <p className="mt-1 text-xs text-white/45">When enabled, AI calls are screened before forwarding upstream. Use the scope setting below to control which traffic is screened.</p>
          </div>
          <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
            checked={form.guardrailsEnabled}
            onChange={(e) => setForm((c) => c ? { ...c, guardrailsEnabled: e.target.checked } : c)} />
        </label>

        {/* All guardrail config — only visible when enabled */}
        {form.guardrailsEnabled && (
          <>
            {/* Scope */}
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Guardrail Scope</span>
              <select
                value={form.guardrailScope}
                onChange={(e) => setForm((c) => c ? { ...c, guardrailScope: e.target.value as PreflightSettings['guardrailScope'] } : c)}
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
              >
                <option value="gateway-only">Gateway traffic only (default) — only Rule42 managed gateway calls</option>
                <option value="all-traffic">All AI traffic — including BYOK providers</option>
              </select>
              <p className="text-xs text-white/40">
                {form.guardrailScope === 'gateway-only'
                  ? 'Only screens calls routed through the Rule42 managed gateway. This protects the upstream OpenRouter account from abuse. BYOK calls (user\'s own keys) are not screened.'
                  : 'Screens ALL AI calls including BYOK providers. More restrictive, adds latency to every call.'}
              </p>
            </label>

            {/* Mode + Engine + Screening toggles */}
            <div className="grid gap-4 md:grid-cols-3">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Mode</span>
                <select
                  value={form.guardrailMode}
                  onChange={(e) => setForm((c) => c ? { ...c, guardrailMode: e.target.value as PreflightSettings['guardrailMode'] } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                >
                  <option value="off">Off</option>
                  <option value="standard">Standard</option>
                  <option value="strict">Strict</option>
                </select>
                <p className="text-xs text-white/40">Standard blocks CSAM, malware, phishing. Strict adds hate, self-harm, high-risk guidance.</p>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Screening Engine</span>
                <select
                  value={form.guardrailEngine}
                  onChange={(e) => setForm((c) => c ? { ...c, guardrailEngine: e.target.value } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                >
                  <option value="llama-guard">Llama Guard</option>
                  <option value="llamafirewall">LlamaFirewall</option>
                  <option value="external">External endpoint</option>
                </select>
              </label>

              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                    checked={form.guardrailInputScreening}
                    onChange={(e) => setForm((c) => c ? { ...c, guardrailInputScreening: e.target.checked } : c)} />
                  Input screening (prompts)
                </label>
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                    checked={form.guardrailOutputScreening}
                    onChange={(e) => setForm((c) => c ? { ...c, guardrailOutputScreening: e.target.checked } : c)} />
                  Output screening (responses)
                </label>
              </div>
            </div>

            {/* === Engine-specific connection config === */}

            {/* Llama Guard */}
            {form.guardrailEngine === 'llama-guard' && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
                <h4 className="text-sm font-semibold text-white/85">Llama Guard Connection</h4>
                <p className="text-xs text-white/40">Llama Guard is Meta's safety classifier. It uses an OpenAI-compatible <code className="text-white/60">/v1/chat/completions</code> endpoint — you send the prompt and it responds with "safe" or "unsafe" + violated categories.</p>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Provider</span>
                    <select
                      value={form.guardrailProvider}
                      onChange={(e) => {
                        const provider = e.target.value as 'openrouter' | 'self-hosted';
                        setForm((c) => c ? {
                          ...c,
                          guardrailProvider: provider,
                          guardrailBaseUrl: provider === 'openrouter' ? 'https://openrouter.ai/api/v1' : c.guardrailBaseUrl,
                          guardrailModel: provider === 'openrouter' ? 'meta-llama/llama-guard-4-12b' : c.guardrailModel,
                        } : c);
                      }}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    >
                      <option value="openrouter">OpenRouter (hosted — no infrastructure needed)</option>
                      <option value="self-hosted">Self-hosted (vLLM / TGI / local)</option>
                    </select>
                    <p className="text-xs text-white/40">
                      {form.guardrailProvider === 'openrouter'
                        ? 'Uses your OpenRouter API key to call Llama Guard hosted by OpenRouter.'
                        : 'Connects to your own vLLM, TGI, or other OpenAI-compatible server running Llama Guard.'}
                    </p>
                  </label>

                  {/* Model: text input for both providers */}
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Model</span>
                    <input
                      type="text"
                      value={form.guardrailModel}
                      onChange={(e) => setForm((c) => c ? { ...c, guardrailModel: e.target.value } : c)}
                      placeholder={form.guardrailProvider === 'openrouter' ? 'meta-llama/llama-guard-4-12b' : 'meta-llama/Llama-Guard-4-12B'}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                    <p className="text-xs text-white/40">
                      {form.guardrailProvider === 'openrouter'
                        ? 'Enter the OpenRouter model slug (e.g. meta-llama/llama-guard-4-12b). Pricing is auto-resolved from OpenRouter on save and applied to catalog models.'
                        : 'The model name your vLLM/TGI instance serves.'}
                    </p>
                  </label>

                  {/* Base URL: only for self-hosted */}
                  {form.guardrailProvider === 'self-hosted' && (
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Base URL</span>
                      <input
                        type="text"
                        value={form.guardrailBaseUrl}
                        onChange={(e) => setForm((c) => c ? { ...c, guardrailBaseUrl: e.target.value } : c)}
                        placeholder="http://localhost:8000/v1"
                        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                      />
                      <p className="text-xs text-white/40">The API base URL (without /chat/completions).</p>
                    </label>
                  )}

                  {/* API Key */}
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">API Key</span>
                    <input
                      type="password"
                      value={guardrailKeyDraft}
                      onChange={(e) => setGuardrailKeyDraft(e.target.value)}
                      placeholder={settings?.secrets?.guardrailApiKey?.configured ? '•••••••• (configured — enter new to rotate)' : (form.guardrailProvider === 'openrouter' ? 'sk-or-v1-... (your OpenRouter key)' : 'API key (if required)')}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                    <div className="flex items-center gap-3 text-xs text-white/40">
                      <span>{settings?.secrets?.guardrailApiKey?.configured ? 'Configured' : 'Not configured'}</span>
                      {settings?.secrets?.guardrailApiKey?.configured && (
                        <button onClick={() => void revealGuardrailKey()} disabled={revealingGuardrailKey} className="text-sky-400 hover:text-sky-300 disabled:opacity-50">
                          {revealingGuardrailKey ? '...' : 'Reveal'}
                        </button>
                      )}
                    </div>
                    {showGuardrailKey && revealedGuardrailKey && (
                      <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs font-mono text-white/60 break-all">
                        {revealedGuardrailKey}
                      </div>
                    )}
                  </label>
                </div>

                {/* Custom categories */}
                <label className="space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Custom Categories (optional)</span>
                  <input
                    type="text"
                    value={form.guardrailCategories}
                    onChange={(e) => setForm((c) => c ? { ...c, guardrailCategories: e.target.value } : c)}
                    placeholder="Leave empty to use model defaults (MLCommons taxonomy: S1-S14)"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                  />
                  <p className="text-xs text-white/40">Override the default hazard categories. Llama Guard 4 uses the MLCommons taxonomy (S1: Violent Crimes, S2: Non-Violent Sexual Content, S3: Hate, S4: Harassment, S5: Self-Harm, S6: Sexual Content Involving Minors, S7: CSAM, S8: Privacy Violations, S9: Intellectual Property, S10: Code Interpreter Abuse, S11: Malware/Hacking, S12: Deception/Disinformation, S13: High-Risk Government Decision-Making, S14: Guns/Weapons).</p>
                </label>

                {/* Pricing info */}
                {form.guardrailProvider === 'openrouter' && (
                  <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 px-4 py-3">
                    <p className="text-xs text-sky-300/70">
                      💡 <strong>Pricing:</strong> The selected Llama Guard model's input cost is automatically resolved from OpenRouter on save and added to each catalog model's cost. Sale price = (model cost + guardrail cost) × (1 + markup%).
                      {resolvingGuardrailPricing && ' ⏳ Resolving pricing and updating catalog...'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* LlamaFirewall */}
            {form.guardrailEngine === 'llamafirewall' && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
                <h4 className="text-sm font-semibold text-white/85">LlamaFirewall Connection</h4>
                <p className="text-xs text-white/40">LlamaFirewall is Meta's multi-scanner framework that combines Llama Guard, Prompt Guard, and code scanners. It runs as a Python service.</p>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Service URL</span>
                    <input
                      type="text"
                      value={form.guardrailBaseUrl}
                      onChange={(e) => setForm((c) => c ? { ...c, guardrailBaseUrl: e.target.value } : c)}
                      placeholder="http://localhost:8000/v1"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                    <p className="text-xs text-white/40">The LlamaFirewall service endpoint URL.</p>
                  </label>

                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">API Key (if required)</span>
                    <input
                      type="password"
                      value={guardrailKeyDraft}
                      onChange={(e) => setGuardrailKeyDraft(e.target.value)}
                      placeholder={settings?.secrets?.guardrailApiKey?.configured ? '•••••••• (configured)' : 'API key for your LlamaFirewall service'}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                    <p className="text-xs text-white/40">{settings?.secrets?.guardrailApiKey?.configured ? 'Configured' : 'Not configured'}</p>
                  </label>
                </div>
              </div>
            )}

            {/* External endpoint */}
            {form.guardrailEngine === 'external' && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
                <h4 className="text-sm font-semibold text-white/85">External Endpoint Connection</h4>
                <p className="text-xs text-white/40">Connect to any external moderation API that accepts a prompt and returns a safe/unsafe classification.</p>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Endpoint URL</span>
                    <input
                      type="text"
                      value={form.guardrailBaseUrl}
                      onChange={(e) => setForm((c) => c ? { ...c, guardrailBaseUrl: e.target.value } : c)}
                      placeholder="https://your-moderation-service.example.com/v1"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                    <p className="text-xs text-white/40">The moderation API endpoint URL.</p>
                  </label>

                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">API Key (if required)</span>
                    <input
                      type="password"
                      value={guardrailKeyDraft}
                      onChange={(e) => setGuardrailKeyDraft(e.target.value)}
                      placeholder={settings?.secrets?.guardrailApiKey?.configured ? '•••••••• (configured)' : 'API key for your moderation service'}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                    <p className="text-xs text-white/40">{settings?.secrets?.guardrailApiKey?.configured ? 'Configured' : 'Not configured'}</p>
                  </label>

                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Model / Path (optional)</span>
                    <input
                      type="text"
                      value={form.guardrailModel}
                      onChange={(e) => setForm((c) => c ? { ...c, guardrailModel: e.target.value } : c)}
                      placeholder="model name or path (if applicable)"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                  </label>
                </div>
              </div>
            )}

            <p className="text-xs text-white/40">Llama Guard is a classifier with false positives/negatives. Rule42 also applies deterministic policy checks. Guardrails are one layer — not a complete safety solution.</p>
          </>
        )}
      </GlassCard>

      {/* === Finance Preflight === */}
      <GlassCard className="space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-indigo-300" />
          <h3 className="text-sm font-semibold text-white">Finance Preflight</h3>
          <Badge variant={form.financePreflightEnabled ? 'success' : 'warning'}>
            {form.financePreflightEnabled ? 'Enabled' : 'Disabled (default)'}
          </Badge>
        </div>

        {/* Enable toggle — always visible */}
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-white/85">Enable finance preflight</p>
            <p className="mt-1 text-xs text-white/45">When enabled, the server checks if the org/user has enough credits before authorizing any AI call. Applies to ALL AI calls, not just managed gateway calls.</p>
          </div>
          <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
            checked={form.financePreflightEnabled}
            onChange={(e) => setForm((c) => c ? { ...c, financePreflightEnabled: e.target.checked } : c)} />
        </label>

        {/* All finance config — only visible when enabled */}
        {form.financePreflightEnabled && (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Fail Mode</span>
                <select
                  value={form.financeFailMode}
                  onChange={(e) => setForm((c) => c ? { ...c, financeFailMode: e.target.value as PreflightSettings['financeFailMode'] } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                >
                  <option value="fail-open">Fail-open (allow + warn)</option>
                  <option value="fail-closed">Fail-closed (block)</option>
                </select>
                <p className="text-xs text-white/40">What to do when the credit check itself fails (e.g. billing service is down).</p>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Timeout (ms)</span>
                <input
                  type="number"
                  value={form.financeTimeoutMs}
                  onChange={(e) => setForm((c) => c ? { ...c, financeTimeoutMs: Number(e.target.value) } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                />
                <p className="text-xs text-white/40">How long to wait for the credit check before applying the fail mode.</p>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Entitlement Cache TTL (ms)</span>
                <input
                  type="number"
                  value={form.financeCacheTtlMs}
                  onChange={(e) => setForm((c) => c ? { ...c, financeCacheTtlMs: Number(e.target.value) } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                />
                <p className="text-xs text-white/40">How long to cache the credit balance before re-checking.</p>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Minimum Credit Threshold (USD)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.financeMinCreditThreshold}
                  onChange={(e) => setForm((c) => c ? { ...c, financeMinCreditThreshold: Number(e.target.value) } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                />
                <p className="text-xs text-white/40">Minimum credit balance required. If below this amount, the call is denied. Set to 0 to only require enough for the estimated cost.</p>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Low Credit Warning Level (USD)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.financeWarningLevel}
                  onChange={(e) => setForm((c) => c ? { ...c, financeWarningLevel: Number(e.target.value) } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                />
                <p className="text-xs text-white/40">When balance is below this but above the minimum, the call is authorized with a warning. Set to 0 to disable.</p>
              </label>
            </div>

            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
              <p className="text-xs text-amber-300/80">
                ⚠️ <strong>Keep this disabled</strong> until the billing mechanism and credit tracker system are complete.
                Enabling without billing means the preflight will fail-open (allow all calls) since there's no billing service to check against.
              </p>
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
}
